import React from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearError,
  refreshCurrentUser,
  requestEmailVerificationOtp,
  verifyEmailVerificationOtp,
} from '../../store/slices/authSlice';

const EmailVerificationPrompt = ({
  email,
  description,
  onVerified,
  className = '',
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const [verificationEmail, setVerificationEmail] = React.useState(email || '');
  const [requestedEmail, setRequestedEmail] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [requestToken, setRequestToken] = React.useState('');
  const [maskedEmail, setMaskedEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    setVerificationEmail(email || '');
  }, [email]);

  const normalizeVerificationError = (value) => {
    const text = String(value || '');

    if (/email does not match otp request|different email|email.*match/i.test(text)) {
      return 'This OTP was not sent to this email. Please request a new code.';
    }

    return text;
  };

  const restartFlow = () => {
    setRequestToken('');
    setRequestedEmail('');
    setMaskedEmail('');
    setOtp('');
    setMessage('');
    setError('');
  };

  const handleSendCode = async () => {
    if (loading) return;
    const emailToVerify = (requestToken ? requestedEmail : verificationEmail).trim().toLowerCase();
    setRequestToken(''); setOtp('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToVerify)) {
      setError('Enter a valid email address.');
      return;
    }

    dispatch(clearError());
    setError('');
    setMessage('');
    const result = await dispatch(requestEmailVerificationOtp({ email: emailToVerify }));

    if (result.error) {
      const nextError = normalizeVerificationError(result.payload || 'Failed to send verification code');

      if (/already verified/i.test(nextError)) {
        await dispatch(refreshCurrentUser());
        onVerified?.();
        return;
      }

      setError(nextError);
      return;
    }

    const token =
      result.payload?.requestToken ||
      result.payload?.otpToken ||
      result.payload?.request_token ||
      result.payload?.otp_token ||
      '';
    setRequestToken(token);
    setRequestedEmail(emailToVerify);
    setVerificationEmail(emailToVerify);
    setMaskedEmail(result.payload?.maskedEmail || result.payload?.masked_email || '');
    setMessage(result.payload?.message || 'Verification code sent to your email.');
  };

  const handleVerify = async () => {
    if (!requestToken) {
      setError('Please send a verification code first.');
      return;
    }

    if (loading) return;
    if (!/^\d{4,8}$/.test(otp)) {
      setError('Enter the 4?8 digit OTP sent to your email.');
      return;
    }

    dispatch(clearError());
    setError('');
    setMessage('');
    const result = await dispatch(
      verifyEmailVerificationOtp({
        email: requestedEmail,
        otp: otp.trim(),
        requestToken,
      })
    );

    if (result.error) {
      const nextError = normalizeVerificationError(result.payload || 'Failed to verify email');

      if (/already verified/i.test(nextError)) {
        await dispatch(refreshCurrentUser());
        restartFlow();
        onVerified?.();
        return;
      }

      setError(nextError);
      return;
    }

    setMessage(result.payload?.message || 'Email verified successfully.');
    setOtp('');
    setRequestToken('');
    setRequestedEmail('');
    setMaskedEmail('');
    onVerified?.();
  };

  return (
    <div className={`email-verification-prompt ${className}`}>
      <Alert variant="warning" className="mb-3">
        {description}
      </Alert>
      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      <Form.Group className="mb-3" controlId="verification-email">
        <Form.Label>Email Address</Form.Label>
        <Form.Control
          type="email"
          value={requestToken ? requestedEmail : verificationEmail}
          onChange={(event) => {
            restartFlow();
            setVerificationEmail(event.target.value);
            setError('');
          }}
          disabled={loading}
        />
      </Form.Group>

      {maskedEmail && (
        <Alert variant="info" className="py-2">
          Code sent to {maskedEmail}.
        </Alert>
      )}

      {requestToken && (
        <Form.Group className="mb-3" controlId="verification-email-otp">
          <Form.Label>OTP</Form.Label>
          <Form.Control
            value={otp}
            onChange={(event) => {
              setOtp(event.target.value.replace(/\D/g, '').slice(0, 8));
              setError('');
            }}
            placeholder="Enter OTP"
            disabled={loading}
          />
        </Form.Group>
      )}

      <div className="d-flex flex-wrap gap-2">
        <Button variant={requestToken ? 'outline-primary' : 'primary'} onClick={handleSendCode} disabled={loading || (!requestToken && !verificationEmail.trim())}>
          {loading && !requestToken ? <Spinner size="sm" className="me-2" /> : null}
          {requestToken ? 'Resend Code' : 'Send Code'}
        </Button>
        {requestToken && (
          <Button variant="outline-secondary" onClick={restartFlow} disabled={loading}>
            Use Different Email
          </Button>
        )}
        {requestToken && (
          <Button variant="primary" onClick={handleVerify} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-2" /> : null}
            Verify Email
          </Button>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationPrompt;
